import {useEffect, useState, useMemo} from "react"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useTeamContext} from "../../reactContext/contextCustomHooks/useTeamContext"
import axios from "axios"
import {API_PROJECTS} from "../../constants/apiUrls"
import {useTranslation} from "react-i18next"
import {useForm} from "react-hook-form"
import Dropzone from "../../components/dropzone/Dropzone"
import useTeam from "../../swrCustomHooks/useTeam"
import useStProjectSource from "../../swrCustomHooks/useStProjectSource"
import LoadingComponent from "../../components/loading-component/LoadingComponent"
import {toast} from "react-toastify"
import Select from "react-select"
import {labelMap} from "../../constants/multilingual"
import isLicenceValidity from "../../tools/isLicenceValidity"

export default function UpdateStProjectsForm({project, mutateStProject, setWorkflowNavigation, setIsPulling}) {
    const {t} = useTranslation()
    const {sessionContext} = useSessionContext()
    const {teamContext} = useTeamContext()
    const {team, isLoadingTeam, isErrorTeam} = useTeam(sessionContext.idTeam)
    const {stProjectSource, isLoadingStProjectSource, isErrorStProjectSource} = useStProjectSource(project.id)
    const languages = team?.team?.languages || []
    const projectDestinationLanguages = project?.destination_languages || []
    const {register, handleSubmit, formState: {errors}} = useForm({defaultValues: {
        isPrivate: !project.is_public,
        title: project.title,
    }})

    const allLanguages = useMemo(() => {
        return languages
            .map((lang) => ({
                value: lang.code,
                label: t(labelMap[lang.code]),
            }))
    }, [languages, labelMap, t])

    const allDestinationLanguages = useMemo(() => {
        return projectDestinationLanguages
            .map((lang) => ({
                value: lang.code,
                label: t(labelMap[lang.code]),
            }))
    }, [projectDestinationLanguages, labelMap, t])

    const [sourceLanguage, setSourceLanguage] = useState(allLanguages.find(x => x.value === project.lang_code))
    const [isSourceLanguage, setIsSourceLanguage] = useState(true)
    const [destinationLanguages, setDestinationLanguages] = useState(allDestinationLanguages)
    const [isDestinationLanguages, setIsDestinationLanguages] = useState(true)
    const [uploadFile, setUploadFile] = useState(null)
    const [isUpdated, setIsUpdated] = useState(false)

    const filteredSourceOptions = useMemo(() => {
        return allLanguages.filter(
            (lang) => !destinationLanguages.some((destLang) => destLang.value === lang.value)
        ).sort((a, b) => a.label.localeCompare(b.label))
    }, [allLanguages, destinationLanguages])

    const filteredDestinationOptions = useMemo(() => {
        return allLanguages.filter((lang) => lang.value !== sourceLanguage?.value).sort((a, b) => a.label.localeCompare(b.label))
    }, [allLanguages, sourceLanguage])

    const handleFileDrop = (file) => {
        setUploadFile(file)
        setIsUpdated(true)
    }

    const updateProject = async (data) => {
        if (isUpdated) {
            if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
                if (sourceLanguage !== undefined && destinationLanguages.length > 0) {
                    setIsSourceLanguage(true)
                    setIsDestinationLanguages(true)
                    let destinationLanguagesValues = []
                    for (let element of destinationLanguages) {
                        destinationLanguagesValues.push(element.value)
                    }
                    const bodyRequest = {
                        is_public: !data.isPrivate,
                        title: data.title,
                        lang_code: sourceLanguage.value,
                        destination_languages: destinationLanguagesValues,
                    }
                    const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}
                    axios.put(`${API_PROJECTS}/${project.id}`, bodyRequest, headersRequest)
                    .then(() => {
                        if (uploadFile) {
                            setIsPulling(true)
                            axios.post(`${API_PROJECTS}/${project.id}/tries`, {step: "upload", filename: uploadFile.name},  headersRequest)
                            .then((response) => {
                                axios.put(response.data.uploadUrl, uploadFile)
                                .then(() => {
                                    toast.success(t("Upload completed!"))
                                })
                                .catch((error) => {
                                    console.error("PUT /projects/uploadUrl: ", error)
                                    toast.error(t("An error has occurred"))
                                })
                            })
                            .catch((error) => {
                                console.error("POST /projects/tries: ", error)
                            })
                            setUploadFile(null)
                        }
                        mutateStProject()
                        toast.success(t("The changes have been applied!"))
                        setWorkflowNavigation(1)
                        setIsUpdated(false)
                    })
                    .catch((error) => {
                        console.error("PUT /projects/project: ", error)
                        toast.error(t("An error has occurred"))
                    })
                } else {
                    setIsSourceLanguage(sourceLanguage === undefined ? false : true)
                    setIsDestinationLanguages(destinationLanguages.length === 0 ? false : true)
                }
            } else {
                toast.error(t("Licence expired. Please contact Neurowide sales department."))
            }
        }
    }

    useEffect(() => {
        setSourceLanguage(allLanguages.find(x => x.value === project.lang_code) || null)
    }, [allLanguages, project.lang_code])

    useEffect(() => {
        setDestinationLanguages((prev) => {
            const selectedValues = prev.map(d => d.value)
            return allDestinationLanguages.filter(lang => selectedValues.includes(lang.value))
        })
    }, [allDestinationLanguages])

    if (isErrorTeam || isErrorStProjectSource || isLoadingTeam || isLoadingStProjectSource || team.message) return <LoadingComponent/>
    return (
        <>
         <form className="update-projects-form" onSubmit={handleSubmit(updateProject)}>
            <div className="left-content">
                <h3>{t("Project information")}</h3>
                <span className="switch-label">{t("Private")}</span>
                <label className="switch" title={t("Manage project visibility")}>
                    <input type="checkbox" id="is-private" name="is-private" {...register("isPrivate", {type: Boolean, onChange: () => {setIsUpdated(true)}})}/>
                    <span className="slider round"></span>
                </label>
                <div className="field">
                    <label>{`${t("Project name")}*`}</label>
                    <input className={`${errors.title ? "not-valid" : ""}`} type="text" id="title" name="title" placeholder={t("Ex. Project 01")} {...register("title", {required: true, minLength: 2, maxLength: 75, onChange: () => {setIsUpdated(true)}})}/>
                </div>
                <label>{`${t("Source language")}*`}</label>
                <Select
                    name="source-language"
                    value={sourceLanguage}
                    options={filteredSourceOptions}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={project.transcription_status === "done" && true}
                    maxMenuHeight={110}
                    className={`basic-single ${!isSourceLanguage && "select-not-valid"}`}
                    classNamePrefix="select"
                    onChange={(value) => {
                        setSourceLanguage(value)
                        setIsUpdated(true)
                    }}
                />
                <label>{`${t("Destination languages")}*`}</label>
                <Select
                    value={filteredDestinationOptions.filter(opt => destinationLanguages.some(sel => sel.value === opt.value))}
                    placeholder={t("Select destination languages")}
                    isMulti
                    name="destination-languages"
                    options={filteredDestinationOptions}
                    closeMenuOnSelect={false}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={project.transcription_status === "done" && true}
                    maxMenuHeight={110}
                    styles={project.transcription_status === "done" && {multiValueRemove: (base) => ({...base, display: "none"})}}
                    className={`basic-multi-select ${!isDestinationLanguages && "select-not-valid"}`}
                    classNamePrefix="select"
                    onChange={(value) => {
                        setDestinationLanguages(value || [])
                        setIsUpdated(true)
                    }}
                />
                <span className="mandatory">{`*${t("Required fields")}`}</span>
            </div>
            <div className="right-content">
                {uploadFile ?
                    <><video controls crossOrigin="anonymous" src={URL.createObjectURL(uploadFile)} type="video/mp4"></video></>
                    :
                    <><video controls crossOrigin="anonymous" src={stProjectSource} type="video/mp4"></video></>
                }
                <Dropzone
                    type={"st"}
                    setUploadFile={handleFileDrop}
                    maxSize={500000000}
                    maxDuration={team.team && team.team.max_duration * 60}
                    transcriptionStatus={project.transcription_status}
                    fileName={project.filename}
                    uploadStatus={project.upload_status}
                    pdf={false}
                />
            </div>
            <div className="buttons">
                <button className="create-project-btn" title={t("Update")}>{t("Update")}</button>
            </div>
        </form>
        </>
    )
}